import { inject } from 'mobx-react';
import { observable } from 'mobx';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';

import { SearchFormFields } from 'models';

import { UitkDialogAnimationType, UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkTertiaryButton } from 'uitk-react-button';
import { UitkToolbar } from 'uitk-react-toolbar';

import { SearchForm } from 'components/common/search-form';
import { SearchFormBottomSection } from 'components/search/search-form-bottom-section';

export interface FiltersDialogProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  onDismiss: () => void;
  searchCriteria: SearchCriteriaModel;
  searchResults: SearchResultsStore;
  filters: SearchFormFields;
  dialogTitle: string;
}

@withLocalization
@inject('analytics')
export class FiltersDialog extends React.Component<FiltersDialogProps> {
  constructor(props: FiltersDialogProps) {
    super(props);

    this.clearFilters = this.clearFilters.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.formRefCallback = this.formRefCallback.bind(this);
    this.getSearchFormBottomSection = this.getSearchFormBottomSection.bind(this);
    this.getSearchFormTopSection = this.getSearchFormTopSection.bind(this);
  }

  private buttonRef: any = React.createRef();
  @observable
  private formRef: any = null;

  private formRefCallback(ref) {
    if (ref) {
      this.formRef = ref.wrappedInstance ? ref.wrappedInstance : ref;
    } else {
      this.formRef = null;
    }
  }

  private closeDialog() {
    if (typeof this.props.onDismiss === 'function') {
      this.props.onDismiss();
    }
  }

  private clearFilters() {
    const searchForm = this.formRef;

    if (searchForm && typeof searchForm.clearDisplayfields === 'function') {
      searchForm.clearDisplayfields();
    }
  }

  private getSearchFormTopSection() {
    const { analytics, l10n, dialogTitle } = this.props;
    const displayFieldsActiveLength = this.formRef && this.formRef.displayFieldsActiveLength;
    const clearButtonActive = displayFieldsActiveLength > 0;
    const toolbarProps = {
      action: this.closeDialog,
      iconLabel: l10n.formatText('hotels.searchResults.filtersDialog.closeButton.label'),
      header: dialogTitle,
      analytics: analytics && analytics.setupTracker('CloseFilterDialog', 'close search filters dialog'),
      actionContent: (
        <UitkTertiaryButton
          type="reset"
          disabled={!clearButtonActive}
          onClick={this.clearFilters}
          text={<LocalizedText message="hotels.searchResults.filtersDialog.clearButton.label" />}
        />
      )
    };

    return <UitkToolbar {...toolbarProps} />;
  }

  private getSearchFormBottomSection() {
    return <SearchFormBottomSection buttonRef={this.buttonRef} />;
  }

  public render() {
    const { searchResults, filters } = this.props;
    const searchResultsCriteria = searchResults && searchResults.searchCriteria;
    const sortAndFilter = searchResults && searchResults.sortAndFilter;
    const sortAndFilterOptions = sortAndFilter && sortAndFilter.options;
    const sortAndFilterDefaults = sortAndFilter && sortAndFilter.defaults;
    const displayFields = filters && filters.displayFields;
    const ignoreFields = filters && filters.ignoreFields;
    const dialogProps = {
      dialogShow: true,
      animation: UitkDialogAnimationType.SLIDEUP
    };

    // field name override will not run without BFF bucketed displayFields
    const fieldNameOverrides = {
      amenityIcons: 'amenities' // Exp 28075: LS_Amenity_Filter_Improvement
    };

    return (
      <UitkFullscreenDialog {...dialogProps}>
        <SearchForm
          className="all-b-padding-twelve uitk-dialog__search-form"
          formRef={this.formRefCallback}
          model={searchResultsCriteria}
          sortAndFilterOptions={sortAndFilterOptions}
          sortAndFilterDefaults={sortAndFilterDefaults}
          renderSearchFormTopSection={this.getSearchFormTopSection}
          renderSearchFormBottomSection={this.getSearchFormBottomSection}
          fieldsWrapper={{
            Tag: UitkDialogContent
          }}
          provideDefaultSubmitButton={false}
          displayFields={displayFields}
          ignoreFields={ignoreFields}
          fieldNameOverrides={fieldNameOverrides}
          searchable={true}
        />
        <span className="is-visually-hidden">
          <LocalizedText message="hotels.searchResults.filtersDialog.a11yLabel" />
        </span>
      </UitkFullscreenDialog>
    );
  }
}
